// Third-party
import React from 'react';
import moment from 'moment';
import { isEqual } from 'lodash';

// Components
import { HoldingIcon } from '../../common';

// Utils
import { enumerateArray, enumerateJSX, uniqueArray, truncateString } from '../../../../utils/objects';
import { formatMessageWithLocale } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import ComparisonItemType from '@/types/comparison-item-type';
import ComparisonHolding from '@/types/comparison-holding';
import Locale from '@/types/locale';

type DisclaimerData = {
  id: string;
  name: string;
  color?: string;
  dates: Date[];
  type: ComparisonItemType;
  holdings: ComparisonHolding[];
};

interface Props {
  globalPrefix: string;
  itemPrefix: string;
  itemDates: DisclaimerData[];
  showItemNames?: boolean;
  locale?: Locale;
  showItemNamesAfterGlobalPrefix?: boolean;
}

class DatesDisclaimer extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  formatDates = (dates: Date[]) => {
    const { locale } = this.props;
    const formattedDates = dates.map(d => moment.utc(d).format(formatMessageWithLocale('Common.DateFormats.Short', locale)));
    return enumerateArray(uniqueArray(formattedDates), formatMessageWithLocale('Common.And', locale));
  };

  getHoldingIcon = (type: ComparisonItemType, color?: string) => (!color ? null : <HoldingIcon type={type} color={color} />);

  getNameElement = (item: DisclaimerData) => {
    let label = truncateString(item.name, 40);

    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO && item.type !== COMPARISON_ITEM_TYPES.GROUP && item.holdings[0].symbol) {
      label = item.holdings[0].symbol;
    }

    return (
      <span key={item.id} className="item-name" data-private data-dd-privacy="mask">
        {this.getHoldingIcon(item.type, item.color)}
        {label}
      </span>
    );
  };

  render() {
    const { itemDates, globalPrefix, itemPrefix, showItemNames, locale, showItemNamesAfterGlobalPrefix, ...rest } = this.props;

    const localShowItemNames: boolean = showItemNames === undefined ? true : showItemNames;

    const mergedDates = itemDates.reduce(
      (acc, item) => {
        if (!item.dates.some(d => !!d)) {
          return acc;
        }

        const existing = acc.find(i => i.dates.every(date => item.dates.some(d => moment(d).isSame(moment(date), 'day'))));

        if (!existing) {
          return acc.concat({
            items: [item],
            dates: item.dates,
          });
        }

        existing.items.push(item);
        return acc;
      },
      [] as { items: DisclaimerData[]; dates: Date[] }[],
    );

    if (mergedDates.length === 0) {
      return null;
    }

    return (
      <div className="dates-disclaimer disclaimer">
        {globalPrefix}

        {enumerateJSX(
          mergedDates.map(mergedDate => (
            <div className="disclaimer-item" key={mergedDate.dates.join()}>
              {showItemNamesAfterGlobalPrefix &&
                locale &&
                enumerateJSX(mergedDate.items.map(this.getNameElement), formatMessageWithLocale('Common.And', locale))}

              <span>{` ${itemPrefix} ${this.formatDates(mergedDate.dates)}`}</span>

              {localShowItemNames && !showItemNamesAfterGlobalPrefix && (
                <>
                  {` ${formatMessageWithLocale('Common.For', locale)} `}
                  {enumerateJSX(mergedDate.items.map(this.getNameElement), formatMessageWithLocale('Common.And', locale))}
                </>
              )}
            </div>
          )),
          formatMessageWithLocale('Common.And', locale),
        )}

        <span>. </span>
      </div>
    );
  }
}

export default DatesDisclaimer;
